import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { connectionRows, CreateIssueDataDocument } from "#app/lib/data";
import type {
  IssueViewStage,
  IssueTypeWithFields,
} from "#app/components/issue-view/issue-view-types";

export function useCreateIssueData(workflowId: string) {
  const variables = useMemo(() => ({ workflowId }), [workflowId]);

  const { data, loading, error } = useQuery(CreateIssueDataDocument, {
    variables,
    skip: !workflowId,
    fetchPolicy: "cache-and-network",
  });

  const issueTypes = useMemo<IssueTypeWithFields[]>(
    () => connectionRows(data?.issueTypes),
    [data?.issueTypes],
  );

  const stages = useMemo<IssueViewStage[]>(
    () => connectionRows(data?.stages),
    [data?.stages],
  );

  // The workspace default — the server's own fallback, so the preselected
  // type is always the one an omitted issueTypeId would have landed on. A
  // project must have a type, so a catalog with no flag still preselects
  // its first type rather than leave the slot empty.
  const defaultIssueTypeId = useMemo(
    () =>
      (issueTypes.find((it) => it.isDefault) ?? issueTypes[0])?.issueTypeId ??
      null,
    [issueTypes],
  );

  const defaultStageId = useMemo(() => {
    if (!data?.stages?.edges) return null;
    const defaultStage = data.stages.edges.find(
      (edge) => edge.node.isDefault,
    );
    return defaultStage?.node.stageId ?? data.stages.edges[0]?.node.stageId ?? null;
  }, [data?.stages]);

  return { issueTypes, stages, defaultIssueTypeId, defaultStageId, loading, error };
}
